import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { gsap } from "../../lib/gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function Experience3D() {
  const mountRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  /* ================= MOBILE DETECTION ================= */

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  /* ================= THREE.JS ONLY FOR DESKTOP ================= */

  useEffect(() => {
    if (isMobile) return; // STOP everything on mobile

    const container = mountRef.current;

    let scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0b0f1a);

    let camera = new THREE.PerspectiveCamera(
      40,
      container.clientWidth / container.clientHeight,
      0.1,
      200
    );

    camera.position.set(0, 0.6, 7);
    camera.lookAt(0, -1.1, 0);

    let renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: "high-performance",
    });

    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;

    container.appendChild(renderer.domElement);

    /* LIGHT */
    scene.add(new THREE.AmbientLight(0xffffff, 0.8));

    const dirLight = new THREE.DirectionalLight(0xffffff, 2);
    dirLight.position.set(5, 10, 5);
    scene.add(dirLight);

    /* ROAD */
    const road = new THREE.Mesh(
      new THREE.PlaneGeometry(60, 8),
      new THREE.MeshStandardMaterial({ color: 0x111827 })
    );
    road.rotation.x = -Math.PI / 2;
    road.position.y = -1.2;
    scene.add(road);

    for (let i = -30; i < 30; i += 5) {
      const dash = new THREE.Mesh(
        new THREE.PlaneGeometry(3, 0.2),
        new THREE.MeshBasicMaterial({ color: 0xffffff })
      );
      dash.rotation.x = -Math.PI / 2;
      dash.position.set(i, -1.18, 0);
      scene.add(dash);
    }

    /* CAR */
    const loader = new GLTFLoader();
    let car;

    const startX = -8;
    const pedestrianWorldX = 4;
    const safeDistance = 9;
    const stopX = pedestrianWorldX - safeDistance;
    loader.load("https://djhpnus2dbkyu.cloudfront.net/CitriotWebsite/car-compressed.glb", (gltf) => {
    // loader.load("/models/car-compressed.glb", (gltf) => {
      car = gltf.scene;

      const box = new THREE.Box3().setFromObject(car);
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const scale = 4 / maxDim;
      car.scale.setScalar(scale);

      const center = box.getCenter(new THREE.Vector3());
      car.position.sub(center);

      car.position.y = -1.1;
      car.position.x = startX;
      car.rotation.y = Math.PI / 2;

      scene.add(car);

      gsap.to(car.position, {
        x: stopX,
        ease: "power2.out",
        scrollTrigger: {
          trigger: container,
          start: "top bottom",
          end: "bottom top",
          scrub: 1.2,
        },
      });
    });

    /* RENDER LOOP */
    let frameId;
    const animate = () => {
      renderer.render(scene, camera);
      frameId = requestAnimationFrame(animate);
    };
    animate();

    /* RESIZE */
    const handleResize = () => {
      camera.aspect = container.clientWidth / container.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(container.clientWidth, container.clientHeight);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", handleResize);
      container.removeChild(renderer.domElement);
      renderer.dispose();
    };
  }, [isMobile]);

  /* ================= MOBILE FALLBACK ================= */

  if (isMobile) {
    return null;
  }

  /* ================= DESKTOP SECTION ================= */

  return (
    <section className="relative h-[75vh] bg-slate-900 overflow-hidden">
      <div ref={mountRef} className="w-full h-full" />

      <img
        src="/images/person.png"
        alt="Pedestrian"
        className="absolute bottom-[200px] right-[46%] h-[240px] z-10 pointer-events-none"
      />
    </section>
  );
}